// hooks/useAnalytics.js
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GA4_MEASUREMENT_ID } from '../lib/config';
import analytics from '../lib/analytics';

/**
 * Hook to automatically track page views on route changes
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (!analytics.hasConsent()) return;
    if (GA4_MEASUREMENT_ID) {
      analytics.init(GA4_MEASUREMENT_ID);
    }
    analytics.pageview(location.pathname + location.search);
  }, [location]);
};

/**
 * Track custom events
 * @param {string} eventName - The name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  analytics.event(eventName, eventParams);
};

/**
 * Track user signup
 */
export const trackSignup = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
  });
};

/**
 * Track user login
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Track competition join
 */
export const trackJoinCompetition = (competitionId, competitionType, league) => {
  trackEvent('join_competition', {
    competition_id: competitionId,
    competition_type: competitionType,
    league: league,
  });
};

/**
 * Track team selection
 */
export const trackTeamSelection = (competitionId, team, matchday) => {
  trackEvent('make_selection', {
    competition_id: competitionId,
    team: team,
    matchday: matchday,
  });
};

/**
 * Track Scout entry submission
 */
export const trackScoutEntry = (competitionId, matchday, ticketType) => {
  trackEvent('submit_scout_entry', {
    competition_id: competitionId,
    matchday: matchday,
    ticket_type: ticketType, // 'footycoach' or 'referral'
  });
};

/**
 * Track referral link copy
 */
export const trackReferralCopy = (referralCode) => {
  trackEvent('copy_referral_link', {
    referral_code: referralCode,
  });
};

/**
 * Track referral signup
 */
export const trackReferralSignup = (referralCode) => {
  trackEvent('referral_signup', {
    referral_code: referralCode,
  });
};

/**
 * Track push notification subscription
 */
export const trackPushSubscription = (action) => {
  trackEvent('push_notification', {
    action: action, // 'subscribe' or 'unsubscribe'
  });
};

/**
 * Track competition creation (admin)
 */
export const trackCreateCompetition = (competitionType, league) => {
  trackEvent('create_competition', {
    competition_type: competitionType,
    league: league,
  });
};

/**
 * Track search/filter usage
 */
export const trackSearch = (searchType, searchTerm) => {
  trackEvent('search', {
    search_type: searchType,
    search_term: searchTerm,
  });
};
